'use client';

import { motion } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { PageWhyPropTradingSchema } from '@/sanity/pages/PageWhyPropTradingTemplate';
import { GroqImage } from '@/helpers/Image';

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function TurboRouteSection({
  data,
}: {
  data: PageWhyPropTradingSchema;
}) {
  if (!data?.turboRouteTitle && !data?.turboRouteSteps?.length) return null;

  return (
    <section className="relative w-full flex flex-col items-center bg-white py-[8rem] lg:py-[15rem] px-[2rem] lg:px-[6rem]">
      <div className="w-[min(141.375rem,100%)] flex flex-col gap-[6rem] lg:gap-[8rem]">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease }}
          className="flex flex-col items-center gap-[1.5rem] text-center"
        >
          {data?.turboRouteChip && (
            <p className="mb-0 font-blacker italic text-[2rem] text-black/70 leading-[1.5]">
              {data.turboRouteChip}
            </p>
          )}
          {data?.turboRouteTitle && (
            <div className="richtext prose-h2:text-[4rem] lg:prose-h2:text-[6rem] prose-h2:font-medium prose-h2:leading-[1.15] prose-h2:tracking-[-0.3rem] prose-em:font-blacker prose-em:text-[#003828]">
              <PortableText
                value={data.turboRouteTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </motion.div>

        {!!data?.turboRouteSteps?.length && (
          <div className="flex flex-col gap-[5rem]">
            {data.turboRouteSteps.map((step, i) => (
              <motion.div
                key={step._key ?? i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, ease, delay: 0.1 }}
                className={`flex flex-col lg:pt-0 pt-[3rem] border items-center  border-[rgba(47,47,47,0.1)] lg:min-h-[40.125rem] overflow-hidden${i % 2 === 1 ? ' lg:flex-row-reverse px-[3rem] lg:pl-[3rem]' : ' lg:flex-row px-[3rem] lg:pr-[3rem]'}`}
              >
                {/* Text side */}
                <div className="flex flex-col justify-between lg:min-h-[40.125rem] gap-[3rem] px-[3rem] py-[4rem] lg:px-[7.625rem] lg:py-[6rem] flex-1">
                  {step.title && (
                    <h3 className="mb-0 text-[2.5rem] lg:text-[3.5rem] font-normal leading-[1.4] tracking-[-0.14rem] text-black">
                      {step.title}
                    </h3>
                  )}
                  {!!step.bullets?.length && (
                    <ul className="flex flex-col gap-[1.75rem] list-none p-0 m-0">
                      {step.bullets.map((bullet, bi) => (
                        <li key={bi} className="flex items-start gap-[1.5rem]">
                          <div className="flex-shrink-0 w-[0.75rem] h-[0.75rem] bg-black mt-[0.625rem]" />
                          <p className="mb-0 text-[2rem] leading-[1.33] text-black/70 font-normal">
                            {bullet}
                          </p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Image side */}
                {step.image && (
                  <div className="order-first lg:order-none relative w-full lg:w-[57.125rem] flex-shrink-0 h-[34rem]">
                    <GroqImage
                      src={step.image}
                      alt={step.image.name || step.title || ''}
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{ width: '100%', height: '100%' }}
                    />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
